<template>
  <dialog-sections
    v-if="sectorOnEdit"
    v-model:sector="sectorOnEdit"
    :dialog="true"
    :editing="editing"
    :saved-section-ids="savedIds"
    @cancel="closeDialog"
    @save="saveChanges"
  />
  <v-form ref="form" :disabled="loader.loading">
    <v-row class="me-0">
      <v-card-title>Setores</v-card-title>
      <v-spacer />
      <v-btn
        icon
        color="primary"
        :disabled="loader.loading"
        @click="createSector"
      >
        <v-icon icon="fas fa-plus" />
      </v-btn>
    </v-row>
    <v-row
      v-if="sectors.length > 0"
      class="mt-3"
    >
      <v-col cols="12">
        <vue-draggable-next
          tag="div"
          :list="sectors"
          class="list-group"
          handle=".handle"
          item-key="id"
        >
          <v-btn
            v-for="sector in sectors"
            :key="sector.id"
            class="d-flex justify-space-between py-6 mb-4 text-capitalize"
            variant="outlined"
            width="100%"
            density="compact"
          >
            <div class="d-flex flex-column align-start">
              <div>{{ sector.name }}</div>
              <div class="text-caption">
                Capacidade: {{ getCapacity(sector) }}
              </div>
            </div>
            <template #append>
              <v-icon
                class="mr-4"
                :disabled="loader.loading"
                color="info"
                icon="fa-solid fa-pen"
                size="small"
                @click="openDialog(sector)"
              />
              <v-icon
                class="mr-4"
                :disabled="loader.loading"
                color="error"
                icon="fa-solid fa-trash"
                size="small"
                @click="emit('delete', sector)"
              />
              <v-icon
                class="handle"
                :disabled="loader.loading"
                style="cursor: pointer"
                start
                color="grey-darken-1"
                icon="fa-regular fa-sort"
              />
            </template>
          </v-btn>
        </vue-draggable-next>
      </v-col>
    </v-row>
  </v-form>
</template>

<script lang="ts" setup>
import Sector from '@/types/Sector'
import { SeatingType } from '@/constants/SeatingTypes'
import DialogSections from './dialog-sections.vue'
import { VueDraggableNext } from 'vue-draggable-next'
import { v4 } from 'uuid'
import { onMounted, ref } from 'vue'
import { useLoader } from '@/stores/loader'

const loader = useLoader()

const sectors = defineModel<Sector[]>({ required: true })

const { editing = false, savedIds = new Set<string>() } = defineProps<{
  editing?: boolean
  /** Ids of the Sectors and Sections the API already has */
  savedIds?: Set<string>
}>()

const emit = defineEmits<{ delete: [ Sector ] }>()

function createSector() {
  const sectorId = v4()

  const sector = {
    id: sectorId,
    mapping_id: '',
    name: `Novo Setor - ${sectors.value.length + 1}`,
    capacity: 0,
    active: true,
    order: 0,
    sections: [ {
      id: v4(),
      sector_id: sectorId,
      active: true,
      name: `Seção Única`,
      rows: [],
    } ],
    seating_type: SeatingType.GENERAL,
  } as Sector

  sectors.value.unshift(sector)
}

const sectorOnEdit = ref<Sector>()
const sectorOnEditOriginal = ref<Sector>()

function openDialog(sector: Sector) {
  sectorOnEditOriginal.value = sector

  const copy = JSON.parse(JSON.stringify(sector)) as Sector
  sectorOnEdit.value = copy
}

function closeDialog() {
  sectorOnEdit.value = undefined
  sectorOnEditOriginal.value = undefined

  sectors.value.forEach((sector, index) => {
    sector.order = index
  })
}

function saveChanges() {
  if (!sectorOnEdit.value || !sectorOnEditOriginal.value) return

  const idx = sectors.value.indexOf(sectorOnEditOriginal.value)
  sectors.value[idx] = sectorOnEdit.value

  closeDialog()
}

function getCapacity(sector: Sector) {
  if (sector.capacity !== 0) return sector.capacity
  return sector.sections
    .flatMap(s => s.rows)
    .filter(r => r)
    .reduce((prev, curr) => {
      return prev + Number(curr!.interval.end) - Number(curr!.interval.start) + 1
    }, 0)
}

onMounted(() => {
  if (!editing) {
    createSector()
  }
})

</script>
